/**
 * Completion maths for the PLM screen's two steps and its sample rail.
 *
 * Pulled out of the components because these are RULES, not layout: "is the
 * stereoscopic step done", "do the component percentages add up", "how far
 * through the batch is this sample". They decide what the analyst is told, so
 * they get their own tests rather than being buried in JSX.
 *
 * Added 2026-09-24 for the reference design's progress indicators.
 */
import type { PlmExamInput, PlmObservationInput } from './plm.api';

/** The seven groups the Stereoscopic tab asks about, in screen order. Texture
 *  was one until 2026-10-07; the tab no longer asks for it, so counting it
 *  would leave every new layer one short of "Complete" for good. */
export const STEREO_SECTIONS = [
  'material', 'homogeneity', 'color',
  'stereoscopic', 'friability', 'treatment', 'asbestos',
] as const;

export type StereoSection = typeof STEREO_SECTIONS[number];

/**
 * Which of the seven stereoscopic groups the analyst has actually answered.
 *
 * A group counts as filled when ANY of its controls is set — that mirrors how
 * the form reads to a user ("I have said something about colour"), and it is
 * deliberately not a validation rule: nothing here blocks a save.
 */
export function filledStereoSections(o: PlmObservationInput | null): Set<StereoSection> {
  const out = new Set<StereoSection>();
  if (!o) return out;

  // A description of nothing but blanks is not a description.
  if ((o.materialIds ?? '').trim() || o.positiveStop) out.add('material');
  if (o.homogeneous || o.nonHomogeneous || o.layered || o.otherHomogenity
      || o.homogenityText) out.add('homogeneity');
  if (o.colorBeige || o.colorBlack || o.colorBlue || o.colorBrown || o.colorClear
      || o.colorGray || o.colorGreen || o.colorOrange || o.colorPink || o.colorRed
      || o.colorSilver || o.colorTan || o.colorPurple || o.colorWhite || o.colorYellow
      || o.colorCyan || o.colorMagenta || o.colorTeal || o.colorGold || o.variousColors
      || o.colorText) out.add('color');
  if (o.fibersPresent) out.add('stereoscopic');
  if (o.friability != null) out.add('friability');
  if (o.treatmentDissolve || o.treatmentAsh || o.treatmentGrind || o.treatmentHeat
      || o.treatmentMatrix || o.treatmentNonFriable || o.treatmentSolventTreated
      || o.treatmentTease || o.treatmentOther) out.add('treatment');
  if (o.fiberObservation || o.noAsbestosDetected) out.add('asbestos');

  return out;
}

/** "4 of 7 sections filled" — the caption under the Stereoscopic step. */
export function stereoSummary(o: PlmObservationInput | null): string {
  const n = filledStereoSections(o).size;
  return n === STEREO_SECTIONS.length
    ? 'Complete'
    : `${n} of ${STEREO_SECTIONS.length} sections filled`;
}

/**
 * Sum of the component percentages, ignoring rows that carry no number.
 *
 * Results are free text (legacy stores them as strings and allows "<1", "ND"
 * and similar), so anything unparseable contributes nothing rather than
 * poisoning the total with NaN.
 */
export function examTotal(exams: PlmExamInput[]): number {
  return exams.reduce((sum, e) => {
    const n = Number.parseFloat((e.result ?? '').trim());
    return Number.isFinite(n) ? sum + n : sum;
  }, 0);
}

/** "3 components · totals 100%" — the caption under the PLM exam step. */
export function examSummary(exams: PlmExamInput[]): string {
  const real = exams.filter(e => e.fiberTypeOid || (e.result ?? '').trim());
  if (real.length === 0) return 'No components yet';
  const total = examTotal(exams);
  const pct = Number.isInteger(total) ? String(total) : total.toFixed(1);
  return `${real.length} component${real.length === 1 ? '' : 's'} · totals ${pct}%`;
}

/** True when the percentages add to 100 (within a rounding hair). */
export function examBalances(exams: PlmExamInput[]): boolean {
  return Math.abs(examTotal(exams) - 100) < 0.05;
}

/**
 * The rail's per-sample line: "Not started", or "In progress · Step 1 of 2".
 *
 * `null` for a layer that is not the one on screen and has never been saved —
 * the caller shows "Not started" for those without loading every layer.
 */
export function sampleProgress(
  opts: { hasObservation: boolean; stereoFilled: number; examCount: number },
): { label: string; step: 1 | 2; pct: number } {
  const { hasObservation, stereoFilled, examCount } = opts;
  if (!hasObservation && stereoFilled === 0 && examCount === 0) {
    return { label: 'Not started', step: 1, pct: 0 };
  }
  const step: 1 | 2 = examCount > 0 ? 2 : 1;
  // Step 1 spans the first half of the bar, step 2 the second.
  const pct = step === 1
    ? Math.round((stereoFilled / STEREO_SECTIONS.length) * 50)
    : 50 + Math.min(50, examCount * 25);
  return { label: `In progress · Step ${step} of 2`, step, pct };
}
